/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import {
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	signal,
	TemplateRef,
	ViewChild
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
	GridApi,
	GridOptions,
	GridReadyEvent,
	SortChangedEvent,
	SortDirection
} from "ag-grid-community";
import { debounceTime, filter, map, Subject, switchMap, takeUntil, tap } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../shared/components/input/input.component";
import { PopoverFloatingComponent } from "../../shared/components/popover/popover.component";
import { TableActionsComponent } from "../../shared/components/table/cell-renderers/table-actions/table-actions.component";
import { PaginationComponent } from "../../shared/components/table/pagination/pagination.component";
import { TableComponent } from "../../shared/components/table/table.component";
import { Tenant } from "../../shared/models/http/tenant/tenant.model";
import { UserInfo } from "../../shared/models/http/user/user-info.model";
import { PagedResponse } from "../../shared/models/ui/paged-response.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { TableResponse } from "../../shared/models/ui/table-response.model";
import { DialogService } from "../../shared/services/dialog.service";
import { StorageService } from "../../shared/services/storage.service";
import { UsersService } from "../../shared/services/users.service";
import { getTableIndex } from "../../shared/utils/utils";

@Component({
	selector: "globalictcc-users",
	standalone: true,
	imports: [
		InputComponent,
		ButtonComponent,
		TableComponent,
		PaginationComponent,
		PopoverFloatingComponent,
		DropdownComponent,
		ReactiveFormsModule
	],
	templateUrl: "./users.component.html",
	styleUrl: "./users.component.scss"
})
export class UsersComponent implements OnInit, OnDestroy {
	@ViewChild("filterPopoverContent") public filterPopoverContent: TemplateRef<unknown>;
	@ViewChild("newUserDialogContent") public newUserDialogContent: TemplateRef<unknown>;
	@ViewChild("editUserDialogContent") public editUserDialogContent: TemplateRef<unknown>;

	public usersTableHasError: boolean = false;
	public currentPage: number = 0;
	public pageSize: number = 12;
	public totalItems: number = 0;
	public totalPages: number = 1;
	public pageItems: number = 0;
	public rolesOptions: SelectOption[] = [];

	public gridOptions: GridOptions = {
		columnDefs: [
			{
				headerName: "No.",
				sortable: false,
				valueFormatter: params =>
					getTableIndex(this.currentPage, this.pageSize, params.node?.rowIndex)
			},
			{
				headerName: "Actions",
				sortable: false,
				cellRenderer: TableActionsComponent,
				cellRendererParams: {
					actions: [
						{
							iconName: "edit",
							onClick: this._onEditRow.bind(this)
						}
					]
				}
			},
			{ headerName: "Username", field: "username" },
			{ headerName: "Name", field: "name" },
			{ headerName: "Surname", field: "surname" },
			{ headerName: "Role", field: "role" },
			{ headerName: "Email", field: "email" },
			{ headerName: "Note", field: "note" }
		],
		rowData: [],
		onGridReady: this._onGridReady.bind(this),
		onSortChanged: this._onSortChanged.bind(this)
	};

	public searchControl = new FormControl("");
	public filterForm = new FormGroup({
		role: new FormControl<string[]>([""])
	});
	public newUserForm = new FormGroup({
		name: new FormControl<string>("", [Validators.required]),
		surname: new FormControl<string>("", [Validators.required]),
		username: new FormControl<string>("", [Validators.required]),
		email: new FormControl<string>("", [Validators.required]),
		description: new FormControl<string>(""),
		role: new FormControl<string>("", [Validators.required])
	});
	public editUserForm = new FormGroup({
		name: new FormControl<string>("", [Validators.required]),
		surname: new FormControl<string>("", [Validators.required]),
		username: new FormControl<string>("", [Validators.required]),
		email: new FormControl<string>("", [Validators.required]),
		description: new FormControl<string>(""),
		role: new FormControl<string>("", [Validators.required])
	});

	private _isAddUserSaveDisabled = signal(true);
	private _isEditUserSaveDisabled = signal(false);
	private _tenants: Tenant[] = [];
	private _searchInputTerm: string;
	private _gridApi: GridApi<UserInfo>;
	private _tenantId: string;
	private _sortField?: string;
	private _sortDirection?: SortDirection = "asc";
	private _userToEdit: UserInfo;
	private _destroy$ = new Subject<void>();

	constructor(
		private _usersService: UsersService,
		private _storageService: StorageService,
		private _dialogService: DialogService,
		private _cdr: ChangeDetectorRef
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this._tenants = this._storageService.getTenants();

		this._onAddUserFormStatusChange();
		this._onEditUserFormStatusChange();
		this._onFilterInputChange();
		this._fecthRoles();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Handles the grid ready event.
	 *
	 * This method is called when the grid is ready to be interacted with. It initializes the grid API,
	 * adjusts the column sizes to fit the grid, performs a search for users, and fetches the user data
	 * to update the grid options.
	 *
	 * @param params - The parameters provided by the grid ready event, including the grid API.
	 */
	private _onGridReady(params: GridReadyEvent) {
		this._gridApi = params.api;

		this._fetchUsers().subscribe();
	}

	/**
	 * Initiates a search for users based on the value changes of the search control.
	 *
	 * This method sets up an observable that listens for changes in the search input field,
	 * debounces the input by 500 milliseconds, filters out terms that are either empty or
	 * shorter than 3 characters, and then performs a user search using the filtered term.
	 *
	 * The search results are then used to update the grid options.
	 *
	 * @private
	 * @returns A subscription to the search observable.
	 */
	private _onFilterInputChange() {
		return this.searchControl.valueChanges
			.pipe(
				debounceTime(500),
				filter(filterTerm => !filterTerm || filterTerm.length > 2),
				tap({
					next: filterTerm => {
						this._searchInputTerm = filterTerm || "";
					}
				}),
				switchMap(() => this._fetchUsers()),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Updates the grid options with the provided paged user data.
	 *
	 * @private
	 * @param {TableResponse<PagedResponse<UserInfo>>} table - The paged response containing user information.
	 * @param {UserInfo[]} pagedUsers.contents - The array of user information to be set as row data.
	 * @param {number} pagedUsers.totalPages - The total number of pages available.
	 * @param {number} pagedUsers.totalElements - The total number of user elements.
	 */
	private _updateGridData(table: TableResponse<PagedResponse<UserInfo>>) {
		const { rowData: page, hasError } = table;

		this.usersTableHasError = hasError;

		if (!hasError) {
			this.pageItems = page.contents.length;
			this.totalPages = page.totalPages;
			this.totalItems = page.totalElements;
			this.pageSize = page.size;
		}

		this._gridApi.setGridOption("rowData", hasError ? [] : page.contents);
		this._cdr.detectChanges();
	}

	/**
	 * Handles the sort changed event for the user table.
	 *
	 * This method is triggered when the sorting of columns in the user table changes.
	 * It retrieves the current sort column and direction, updates the corresponding
	 * fields, and fetches the updated list of users.
	 *
	 * @param params - The event parameters containing the sort state of the columns.
	 */
	private _onSortChanged(params: SortChangedEvent) {
		const sortColumn = params.api.getColumnState().find(col => col.sort !== null);
		this._sortField = sortColumn?.colId;
		this._sortDirection = sortColumn?.sort;

		this._fetchUsers().subscribe();
	}

	/**
	 * Fetches a list of users based on the current filter and pagination settings.
	 *
	 * @private
	 * @returns  An observable containing the list of users.
	 *
	 * The method retrieves the following parameters from the component's state:
	 * - `role`: The role filter value from the filter form, defaults to an empty string if not set.
	 * - `applications`: The application filter value from the filter form, defaults to an empty string if not set.
	 * - `this._tenantId`: The tenant ID for the current user.
	 * - `this.currentPage`: The current page number for pagination.
	 * - `this.pageSize`: The number of users to fetch per page.
	 * - `this._sortField`: The field by which to sort the users.
	 * - `this._searchInputTerm`: The search term to filter users.
	 * - `this._sortDirection`: The direction of sorting (ascending or descending).
	 *
	 * These parameters are then passed to the `_usersService.getUsers` method to fetch the users.
	 */
	private _fetchUsers() {
		this._gridApi.setGridOption("loading", true);

		const role = this.filterForm.controls.role.value || [];

		return this._usersService
			.getUsers(
				this._tenantId,
				this.currentPage,
				this.pageSize,
				this._sortField,
				this._searchInputTerm,
				role,
				[],
				this._sortDirection
			)
			.pipe(
				tap({
					next: table => this._updateGridData(table),
					finalize: () => this._gridApi.setGridOption("loading", false)
				})
			);
	}

	/**
	 * Handles the refresh action for the user table.
	 * This method triggers a fetch operation to retrieve the latest user data
	 * and updates the table accordingly.
	 *
	 * @remarks
	 * The method subscribes to the observable returned by the `_fetchUsers` method
	 * to ensure the data retrieval process is executed.
	 */
	public onTableRefresh() {
		this._fetchUsers().subscribe();
	}

	/**
	 * Handles the status changes of the new user form.
	 *
	 * This method subscribes to the `statusChanges` observable of the `newUserForm`
	 * and updates the `_isAddUserSaveDisabled` property based on the form's validity status.
	 * If the form status is "INVALID", `_isAddUserSaveDisabled` is set to true, otherwise false.
	 * The subscription is automatically unsubscribed when the component is destroyed.
	 *
	 * @private
	 */
	private _onAddUserFormStatusChange() {
		this.newUserForm.statusChanges
			.pipe(
				tap({ next: status => this._isAddUserSaveDisabled.set(status === "INVALID") }),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles the status changes of the edit user form.
	 *
	 * This method subscribes to the `statusChanges` observable of the `editUserForm`.
	 * When the status changes, it updates the `_isEditUserSaveDisabled` property
	 * based on whether the form status is "INVALID". The subscription is automatically
	 * cleaned up when the component is destroyed.
	 *
	 * @private
	 */
	private _onEditUserFormStatusChange() {
		this.editUserForm.statusChanges
			.pipe(
				tap({ next: status => this._isEditUserSaveDisabled.set(status === "INVALID") }),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Applies the filter and fetches the filtered list of users.
	 * This method triggers the `_fetchUsers` method and subscribes to its observable.
	 */
	public onFilterApply() {
		this._fetchUsers().subscribe();
	}

	/**
	 * Handles the event of navigating to the next page of users.
	 * Increments the current page number and fetches the users for the new page.
	 * Updates the grid options with the fetched users.
	 */
	public onHandleNextPage() {
		this.currentPage++;

		this._fetchUsers().subscribe();
	}

	/**
	 * Handles the action of navigating to the previous page.
	 * Decrements the current page number and fetches the users for the new page.
	 * Updates the grid options with the fetched users.
	 */
	public onHandlePreviousPage() {
		this.currentPage--;

		this._fetchUsers().subscribe();
	}

	/**
	 * Opens a dialog to add a new user.
	 *
	 * This method uses the dialog service to open a confirmation dialog
	 * with the specified title, content, and buttons. When the "Add" button
	 * is clicked, the `_onCreateUser` method is called to handle the user creation.
	 *
	 * @public
	 */
	public onAddNewUser() {
		this._dialogService.open({
			data: {
				type: "confirm",
				title: "Add User",
				content: this.newUserDialogContent,
				confirmBtnText: "Add",
				confirmAction: this._onCreateUser.bind(this),
				isConfirmActionDisabled: this._isAddUserSaveDisabled
			}
		});
	}

	/**
	 * Handles the creation of a new user.
	 *
	 * This method filters the selected applications from the available applications,
	 * retrieves the tenants from the storage service, and validates the new user form.
	 * If the form is invalid or any required fields are missing, the method returns early.
	 * Otherwise, it constructs a user information object and calls the user service to add the new user.
	 *
	 * @private
	 */
	private _onCreateUser() {
		if (
			this.newUserForm.invalid ||
			this.newUserForm.controls.name.value === null ||
			this.newUserForm.controls.surname.value === null ||
			this.newUserForm.controls.username.value === null ||
			this.newUserForm.controls.email.value === null ||
			this.newUserForm.controls.description.value === null ||
			this.newUserForm.controls.role.value === null
		)
			return;

		const userInfo = {
			name: this.newUserForm.controls.name.value,
			surname: this.newUserForm.controls.surname.value,
			username: this.newUserForm.controls.username.value,
			email: this.newUserForm.controls.email.value,
			description: this.newUserForm.controls.description.value,
			role: this.newUserForm.controls.role.value,
			tenants: this._tenants
		};

		this._usersService
			.addUser(this._tenantId, userInfo)
			.pipe(switchMap(() => this._fetchUsers()))
			.subscribe();
	}

	/**
	 * Handles the edit action for a row in the user table.
	 *
	 * This method is triggered when a user selects the edit option for a specific row.
	 * It sets the selected row data, patches the edit form with the row data, and opens
	 * a dialog for editing the user information.
	 *
	 * @param rowData - The data of the row that is being edited, containing user information.
	 */
	public _onEditRow(rowData: UserInfo) {
		this._userToEdit = rowData;
		this.editUserForm.patchValue({
			...rowData
		});

		this._dialogService.open({
			data: {
				type: "confirm",
				title: "Edit User",
				confirmBtnText: "Save",
				content: this.editUserDialogContent,
				confirmAction: this._onEditUser.bind(this),
				isConfirmActionDisabled: this._isEditUserSaveDisabled
			}
		});
	}

	/**
	 * Handles the editing of a user by gathering form data, validating it, and sending an update request.
	 *
	 * This method performs the following steps:
	 * 1. Filters the selected applications based on the form input.
	 * 2. Retrieves the tenants from the storage service.
	 * 3. Validates the form and required fields.
	 * 4. Constructs a user information object with the form data.
	 * 5. Sends an update request to the user service with the user information.
	 *
	 * @private
	 * @returns  Returns early if the form is invalid or required fields are missing.
	 */
	private _onEditUser() {
		if (
			this.editUserForm.invalid ||
			this.editUserForm.controls.name.value === null ||
			this.editUserForm.controls.surname.value === null ||
			this.editUserForm.controls.username.value === null ||
			this.editUserForm.controls.email.value === null ||
			this.editUserForm.controls.description.value === null ||
			this.editUserForm.controls.role.value === null ||
			this._userToEdit.id === null ||
			this._userToEdit.id == undefined
		)
			return;

		const userInfo = {
			id: this._userToEdit.id,
			name: this.editUserForm.controls.name.value,
			surname: this.editUserForm.controls.surname.value,
			username: this.editUserForm.controls.username.value,
			email: this.editUserForm.controls.email.value,
			description: this.editUserForm.controls.description.value,
			role: this.editUserForm.controls.role.value,
			tenants: this._tenants
		};

		this._usersService
			.updateUser(this._tenantId, userInfo.id, userInfo)
			.pipe(switchMap(() => this._fetchUsers()))
			.subscribe();
	}

	/**
	 * Opens a confirmation dialog to delete a user.
	 *
	 * This method triggers a dialog service to open a confirmation dialog
	 * with options to confirm or cancel the deletion of a user. If the user
	 * confirms the deletion, the `_onConfirmDelete` method is called.
	 *
	 * The dialog displays the user's name in the confirmation message.
	 */
	public handleDelete() {
		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Yes, Delete",
				closeBtnText: "Cancel",
				title: `Are you sure you want to delete the user "${this._userToEdit.name}"?`,
				confirmAction: this._onConfirmDelete.bind(this)
			}
		});
	}

	/**
	 * Handles the confirmation of user deletion.
	 *
	 * This method checks if the selected row data has a valid ID. If the ID is valid,
	 * it calls the deleteUser method from the _usersService to delete the user with the
	 * specified tenant ID and user ID. Upon successful deletion, it triggers the
	 * _afterDeleteConfirmation method.
	 *
	 * @private
	 */
	private _onConfirmDelete() {
		if (this._userToEdit.id === null || this._userToEdit.id === undefined) return;

		this._usersService
			.deleteUser(this._tenantId, this._userToEdit.id)
			.pipe(
				switchMap(() =>
					this._dialogService
						.open({
							data: {
								type: "info",
								closeBtnText: "Ok",
								title: `The user "${this._userToEdit.name}" is successfully deleted`
							}
						})
						.afterClosed()
				),
				tap({ next: () => this._dialogService.closeAll() }),
				switchMap(() => this._fetchUsers())
			)
			.subscribe();
	}

	/**
	 * Fetches roles from the user service and maps them to the rolesOptions property.
	 * Each role is transformed into an object with label and value properties, both set to the role's code.
	 *
	 * @private
	 */
	private _fecthRoles() {
		this._usersService
			.getRoles()
			.pipe(
				map(roles => roles.map(role => ({ label: role.code, value: role.code }))),
				tap({ next: roles => (this.rolesOptions = roles) })
			)
			.subscribe();
	}
}
